import { getStyleDeclaration } from './get-style-declaration.js';
import {
  activeState, correctSelectedState, correctState, focusState, getOutlineDeclaration, getStatesDeclaration, hoverState,
  incorrectSelectedState, justifyContentValueReplace, outliner, parentActiveState, parentFocusState, parentHoverState,
  parentSelectedState, relativeBorderRadius, selectedState
} from './helpers';
import warning from 'fbjs/lib/warning';
import { px2em } from '#/providers/creative/px2em/@impl/px2em.js';

/**
 * Yet another "gem", remap original image fit value into object-fit property.
 */
const remapFit = new Map([
  ['Fit', 'contain'],
  ['Fill', 'cover'],
  ['Stretch', 'fill'],
]);
/**
 * @typedef {Object} OutlineDescription
 * @property {number} borderGap
 * @property {number} borderSize
 * @property {string} borderClor
 */

/**
 * @typedef {Object & OutlineDescription} ResultState
 * @property {number} scale
 * @property {number} opacity
 * @property {OutlineDescription} focus
 */

/**
 * @typedef {Object} QuizOptionResultStates
 * @property {ResultState} incorrectNotSelected
 * @property {ResultState} incorrectSelected
 * @property {ResultState} correctNotSelected
 * @property {ResultState} correctSelected
 */

/**
 * @typedef {Object} ClassDescription
 * @property {[*]} backgrounds
 * @property {number} borderRadius
 * @property {string} type
 * @property {number} width
 * @property {number} height
 * @property {ClassDescription} parent
 * @property {string} selector
 * @property {boolean} [isBold]
 * @property {boolean} [isItalic]
 * @property {boolean} [isUnderline]
 * @property {{default: StyledState, hover: StyledState, click: StyledState}} styledStates
 * @property {QuizOptionResultStates} [quizOptionResultStates]
 */

/**
 *
 * @param {ClassDescription} classDescription
 * @param {function(string): {background: *}} getBackground
 * @return {[]}
 */
export function getClassDeclaration(classDescription, getBackground) {
  const {
    type,
    className,
    // styledStates,
    // height,
    // width,
    // borderRadius,
    placements = [],
    backgrounds = [],
  } = classDescription;
  const selector = (classDescription.selector = `.${className}`);
  const asChildren = (parentSelector) => `${parentSelector} ${selector}`;

  const rules = [];

  rules.push = (...newRules) => {
    newRules.forEach((rule) => {
      const [selector, declarations, media] = rule;
      const valuedDeclarations = declarations.filter((declaration) => {
        const [property, value] = declaration;

        warning(
          value,
          `Skipping "${property}" property for "${selector}" due to missing value`,
        );
        return !!value;
      });
      Array.prototype.push.call(rules, [selector, valuedDeclarations, media]);
    });
  };

  /* assume option backgrounds could be only fill or gradient,
         so it is possible to pack them into one declaration */

  // backgrounds.length > 0 &&
  rules.push([
    selector,
    [
      [
        'background',
        [...backgrounds]
          .reverse()
          .map(getBackground)
          .map(({ background }) => background)
          .toString(),
      ],
    ],
  ]);

  switch (type) {
    case 'vote': {
      rules.push([
        selector,
        [
          [
            'border-radius',
            relativeBorderRadius(
              classDescription.borderRadius,
              classDescription,
            ),
          ],
        ],
      ]);

      const {
        default: base,
        hover,
        click: active,
      } = classDescription.styledStates;

      rules.push([selector, getStatesDeclaration(base)]);
      rules.push([hoverState(selector), getStatesDeclaration(hover)]);
      rules.push([activeState(selector), getStatesDeclaration(active)]);

      /* Outline declarations */
      rules.push([outliner(selector), getOutlineDeclaration(base)]);
      rules.push([
        hoverState(selector).map(outliner),
        getOutlineDeclaration(hover),
      ]);
      rules.push([
        activeState(selector).map(outliner),
        getOutlineDeclaration(active),
      ]);

      rules.push([
        hoverState(selector),
        [
          ['color', 'initial'],
          ['background-color', 'initial'],
          ['z-index', '1'] /* in some case it should overlap own siblings */,
        ],
      ]);

      break;
    }
    case 'result': {
      rules.push([
        selector,
        [
          [
            'border-radius',
            relativeBorderRadius(
              classDescription.borderRadius,
              classDescription,
            ),
          ],
        ],
      ]);

      if (classDescription.pollOptionResultStates) {
        const { pollOptionResultStates: { notSelected: base, selected } = {} } =
          classDescription;

        rules.push([selector, getStatesDeclaration(base)]);
        rules.push([focusState(selector), getStatesDeclaration(base.focus)]);
        rules.push([selectedState(selector), getStatesDeclaration(selected)]);
        rules.push([
          focusState(selectedState(selector)),
          getStatesDeclaration(selected.focus),
        ]);

        /* Outline declaration for matrix: [selected,not-selected] * [default,focused] state */
        rules.push([outliner(selector), getOutlineDeclaration(base)]);
        rules.push([
          focusState(selector).map(outliner),
          getOutlineDeclaration(base.focus),
        ]);
        rules.push([
          outliner(selectedState(selector)),
          getOutlineDeclaration(selected),
        ]);
        rules.push([
          focusState(selectedState(selector)).map(outliner),
          getOutlineDeclaration(selected.focus),
        ]);
      }

      if (
        classDescription.quizOptionResultStates ||
        classDescription.pollOptionResultStates
      ) {
        /**
         @example {
          "incorrectNotSelected": {
          "scale": 1,
          "borderGap": 0,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "opacity": 1,
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 0,
          "borderGap": 0
          }
          },
          "incorrectSelected": {
          "scale": 1,
          "borderGap": 0,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "opacity": 1,
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 0,
          "borderGap": 0
          }
          },
          "correctNotSelected": {
          "scale": 1,
          "borderGap": 0,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "opacity": 1,
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 0,
          "borderGap": 0
          }
          },
          "correctSelected": {
          "scale": 1,
          "borderGap": 0,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "opacity": 1,
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 0,
          "borderGap": 0
          }
          }
          };
         */

        const target =
          classDescription.quizOptionResultStates ||
          classDescription.pollOptionResultStates;

        const base = target['incorrectNotSelected'] || target['notSelected'];
        const answer = target['incorrectSelected'];
        const correct = target['correctNotSelected'];
        const correctAnswer = target['correctSelected'];
        const selected = target['selected'];

        // base
        if (base) {
          rules.push([selector, getStatesDeclaration(base)]);
          rules.push([outliner(selector), getOutlineDeclaration(base)]);
          rules.push([
            focusState(selector).map(outliner),
            getOutlineDeclaration(base.focus),
          ]);
          const bg = base.backgrounds ?? [];
          rules.push([
            selector,
            [
              [
                'background',
                [...bg]
                  .reverse()
                  .map(getBackground)
                  .map(({ background }) => background)
                  .toString(),
              ],
            ],
          ]);
        }

        // answer
        if (answer) {
          rules.push([selectedState(selector), getStatesDeclaration(answer)]);
          rules.push([
            outliner(selectedState(selector)),
            getOutlineDeclaration(answer),
          ]);
          rules.push([
            focusState(selectedState(selector)).map(outliner),
            getOutlineDeclaration(answer.focus),
          ]);
          const bg = answer.backgrounds ?? [];
          rules.push([
            incorrectSelectedState(selector),
            [
              [
                'background',
                [...bg]
                  .reverse()
                  .map(getBackground)
                  .map(({ background }) => background)
                  .toString(),
              ],
            ],
          ]);
        }

        // correct
        if (correct) {
          rules.push([correctState(selector), getStatesDeclaration(correct)]);
          rules.push([
            outliner(correctState(selector)),
            getOutlineDeclaration(correct),
          ]);
          rules.push([
            focusState(correctState(selector)).map(outliner),
            getOutlineDeclaration(correct.focus),
          ]);
          const bg = correct.backgrounds ?? [];
          rules.push([
            correctState(selector),
            [
              [
                'background',
                [...bg]
                  .reverse()
                  .map(getBackground)
                  .map(({ background }) => background)
                  .toString(),
              ],
            ],
          ]);
        }

        // correct answer
        if (correctAnswer) {
          rules.push([
            correctState(selectedState(selector)),
            getStatesDeclaration(correctAnswer),
          ]);
          rules.push([
            outliner(correctState(selectedState(selector))),
            getOutlineDeclaration(correctAnswer),
          ]);
          rules.push([
            focusState(correctState(selectedState(selector))).map(outliner),
            getOutlineDeclaration(correctAnswer.focus),
          ]);
          const bg = correctAnswer.backgrounds ?? [];
          rules.push([
            correctSelectedState(selector),
            [
              [
                'background',
                [...bg]
                  .reverse()
                  .map(getBackground)
                  .map(({ background }) => background)
                  .toString(),
              ],
            ],
          ]);
        }

        if (selected) {
          rules.push([selectedState(selector), getStatesDeclaration(selected)]);
          rules.push([
            outliner(selectedState(selector)),
            getOutlineDeclaration(selected),
          ]);
          rules.push([
            focusState(selectedState(selector)).map(outliner),
            getOutlineDeclaration(selected.focus),
          ]);
          const bg = selected.backgrounds ?? [];
          rules.push([
            selectedState(selector),
            [
              [
                'background',
                [...bg]
                  .reverse()
                  .map(getBackground)
                  .map(({ background }) => background)
                  .toString(),
              ],
            ],
          ]);
        }
      }
      break;
    }
    case 'simpleText': {
      const {
        color,
        fontSize,
        hAlign,
        vAlign,
        isBold,
        isItalic,
        isUnderline,
        lineHeight,
      } = classDescription;

      rules.push(
        ...placements.map(getStyleDeclaration.positions(selector)),
        ...placements.map(getStyleDeclaration.size(selector)),
      );

      const props = [
        ['display', 'flex'],
        ['color', color],
        ['font-size', fontSize],
        ['align-items', vAlign.replace(/start|end/, `flex-${vAlign}`)],
        ['justify-content', justifyContentValueReplace(hAlign)],
        ['text-align', hAlign],
        ['white-space', 'normal'],
        ['font-weight', isBold ? '500' : '400'],
        ['font-style', isItalic ? 'italic' : 'normal'],
        ['text-decoration', isUnderline ? 'underline' : 'none'],
        ['line-height', lineHeight],
        ['z-index', '1'],
      ]; //.filter(([, value]) => !!value);

      rules.push([selector, props]);

      if (classDescription.pollOptionResultStates) {
        const { notSelected: base, selected } =
          classDescription.pollOptionResultStates;

        rules.push(
          [selector, [['color', base.color]]],
          [
            parentSelectedState(classDescription.parent.selector, selector),
            [['color', selected.color]],
          ],
        );
      }
      if (classDescription.quizOptionResultStates) {
        /**
         @example {
          "incorrectNotSelected": {
          "color": "rgba(255, 255, 255, 1)"
          },
          "incorrectSelected": {
          "color": "rgba(255, 51, 51, 1)"
          },
          "correctNotSelected": {
          "color": "rgba(51, 204, 102, 1)"
          },
          "correctSelected": {
          "color": "rgba(51, 204, 102, 1)"
          }
          };
         */
        const {
          quizOptionResultStates: {
            incorrectNotSelected: base, // .result as `selector`
            incorrectSelected: answer, // .result.answer as `selector[data-voted="true"]`
            correctNotSelected: correct, // .result.correct as selector[data-correct="true"]
            correctSelected: correctAnswer, // .result.answer.correct as selector[data-voted="true"][data-correct="true"]
          } = {},
        } = classDescription;

        rules.push(
          [selector, [['color', base.color]]],
          [
            parentSelectedState(classDescription.parent.selector, selector),
            [['color', answer.color]],
          ],
          [
            `${correctState(classDescription.parent.selector)} ${selector}`,
            [['color', correct.color]],
          ],
          [
            `${selectedState(
              correctState(classDescription.parent.selector),
            )} ${selector}`,
            [['color', correctAnswer.color]],
          ],
        );
      }
      break;
    }
    case 'image': {
      const { styledStates, pollOptionResultStates, fit } = classDescription;

      rules.push(
        ...placements.map(getStyleDeclaration.positions(selector)),
        ...placements.map(getStyleDeclaration.size(selector)),
      );
      rules.push([
        `${selector} > img`,
        [
          /*
            // static, declare in image.module.css
             ['width', '100%'],
             ['height', '100%'],
             ['border-radius', 'inherit'],
            */
          ['object-fit', remapFit.get(fit)],
        ],
      ]);

      if (pollOptionResultStates) {
        const { notSelected: base, selected } = pollOptionResultStates;

        rules.push(
          /* image class description has legacy borderRadius, to recalculate it need to pass classDescription as context */
          [selector, getStatesDeclaration(base, classDescription)],
          [outliner(selector), getOutlineDeclaration(base)],
          [
            parentFocusState(classDescription.parent.selector, selector),
            getStatesDeclaration(base.focus),
          ],
          [
            focusState(classDescription.parent.selector)
              .map(asChildren)
              .map(outliner),
            getOutlineDeclaration(base.focus),
          ],
          [
            parentSelectedState(classDescription.parent.selector, selector),
            getStatesDeclaration(selected, classDescription),
          ],
          [
            outliner(
              asChildren(selectedState(classDescription.parent.selector)),
            ),
            getOutlineDeclaration(selected),
          ],
          [
            parentFocusState(
              selectedState(classDescription.parent.selector),
              selector,
            ),
            getStatesDeclaration(selected.focus, classDescription),
          ],
          [
            focusState(selectedState(classDescription.parent.selector))
              .map(asChildren)
              .map(outliner),
            getOutlineDeclaration(selected.focus),
          ],
        );

        let overlaySelector = `${selector}::after`;
        rules.push([
          overlaySelector,
          [
            /*
                      // static properties, declare in image.module.css
                          ['position', 'absolute'],
                          ['width', '100%'],
                          ['height', '100%'],
                          ['top', '0'],
                          ['left', '0'],
                          ['content', ''],
              */
            ['background', [...base.overlays].reverse().join(',')],
          ],
        ]);

        rules.push([
          parentSelectedState(
            classDescription.parent.selector,
            overlaySelector,
          ),
          [['background', [...selected.overlays].reverse().join(',')]],
        ]);
      } else if (styledStates) {
        rules.push(
          /* image class description has legacy borderRadius, to recalculate it need to pass classDescription as context */
          [
            selector,
            getStatesDeclaration(styledStates.default, classDescription),
          ],
          [outliner(selector), getOutlineDeclaration(styledStates.default)],
          [
            parentHoverState(classDescription.parent.selector, selector),
            getStatesDeclaration(styledStates.hover, classDescription),
          ],
          [
            hoverState(classDescription.parent.selector)
              .map(asChildren)
              .map(outliner),
            getOutlineDeclaration(styledStates.hover),
          ],
          [
            parentActiveState(classDescription.parent.selector, selector),
            getStatesDeclaration(styledStates.click, classDescription),
          ],
          [
            activeState(classDescription.parent.selector)
              .map(asChildren)
              .map(outliner),
            getOutlineDeclaration(styledStates.click),
          ],
          // [selectedState(selector), getStatesDeclaration(styledStates.selected, classDescription)],
        );
      }

      if (classDescription.quizOptionResultStates) {
        /**
         @example {
          "incorrectNotSelected": {
          "scale": 1,
          "opacity": 1,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderRadius": 12,
          "borderGap": 0,
          "overlays": [
          "fill_977281"
          ],
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 4,
          "borderGap": 2
          }
          },
          "incorrectSelected": {
          "scale": 1,
          "opacity": 1,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderRadius": 12,
          "borderGap": 0,
          "overlays": [
          "fill_070816"
          ],
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 4,
          "borderGap": 2
          }
          },
          "correctNotSelected": {
          "scale": 1,
          "opacity": 1,
          "borderSize": 0,
          "borderColor": "rgba(34, 187, 85, 1)",
          "borderRadius": 12,
          "borderGap": 0,
          "overlays": [
          "fill_146926"
          ],
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 4,
          "borderGap": 2
          }
          },
          "correctSelected": {
          "scale": 1,
          "opacity": 1,
          "borderSize": 0,
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderRadius": 12,
          "borderGap": 0,
          "overlays": [
          "fill_241075"
          ],
          "focus": {
          "borderColor": "rgba(255, 255, 255, 1)",
          "borderSize": 4,
          "borderGap": 2
          }
          }
          };
         */

        const {
          quizOptionResultStates: {
            incorrectNotSelected: base, // .result as `selector`
            incorrectSelected: answer, // .result.answer as `selector[data-voted="true"]`
            correctNotSelected: correct, // .result.correct as selector[data-correct="true"]
            correctSelected: correctAnswer, // .result.answer.correct as selector[data-voted="true"][data-correct="true"]
          } = {},
        } = classDescription;

        // base
        rules.push([selector, getStatesDeclaration(base, classDescription)]);
        rules.push([outliner(selector), getOutlineDeclaration(base)]);
        rules.push([
          `${focusState(classDescription.parent.selector)} ${selector}`,
          getStatesDeclaration(base, classDescription),
        ]);
        rules.push([
          focusState(classDescription.parent.selector)
            .map(asChildren)
            .map(outliner),
          getOutlineDeclaration(base.focus),
        ]);

        // answer
        rules.push([
          `${selectedState(classDescription.parent.selector)} ${selector}`,
          getStatesDeclaration(answer, classDescription),
        ]);
        rules.push([
          outliner(asChildren(selectedState(classDescription.parent.selector))),
          getOutlineDeclaration(answer),
        ]);
        rules.push([
          focusState(selectedState(classDescription.parent.selector))
            .map(asChildren)
            .map(outliner),
          getOutlineDeclaration(answer.focus),
        ]);

        // correct
        rules.push([
          `${correctState(classDescription.parent.selector)} ${selector}`,
          getStatesDeclaration(correct, classDescription),
        ]);
        rules.push([
          outliner(asChildren(correctState(classDescription.parent.selector))),
          getOutlineDeclaration(correct),
        ]);
        rules.push([
          focusState(correctState(classDescription.parent.selector))
            .map(asChildren)
            .map(outliner),
          getOutlineDeclaration(correct.focus),
        ]);

        // correct answer
        rules.push([
          `${correctState(
            selectedState(classDescription.parent.selector),
          )} ${selector}`,
          getStatesDeclaration(correctAnswer, classDescription),
        ]);
        rules.push([
          outliner(
            asChildren(
              correctState(selectedState(classDescription.parent.selector)),
            ),
          ),
          getOutlineDeclaration(correctAnswer),
        ]);
        rules.push([
          focusState(
            correctState(selectedState(classDescription.parent.selector)),
          )
            .map(asChildren)
            .map(outliner),
          getOutlineDeclaration(correctAnswer.focus),
        ]);

        let overlaySelector = `${selector}::after`;

        rules.push([
          overlaySelector,
          [['background', [...base.overlays].reverse().join(',')]],
        ]);
        rules.push([
          `${selectedState(
            classDescription.parent.selector,
          )} ${overlaySelector}`,
          [['background', [...answer.overlays].reverse().join(',')]],
        ]);
        rules.push([
          `${correctState(
            classDescription.parent.selector,
          )} ${overlaySelector}`,
          [['background', [...correct.overlays].reverse().join(',')]],
        ]);
        rules.push([
          `${correctState(
            selectedState(classDescription.parent.selector),
          )} ${overlaySelector}`,
          [['background', [...correctAnswer.overlays].reverse().join(',')]],
        ]);
      }
      break;
    }
    case 'pollOptionResultBar': {
      rules.push(
        ...placements.map(getStyleDeclaration.positions(selector)),
        ...placements.map(getStyleDeclaration.size(selector)),
      );

      rules.push([
        selector,
        [
          [
            'border-radius',
            relativeBorderRadius(
              classDescription.borderRadius,
              classDescription,
            ),
          ],
        ],
      ]);

      const chartDecorations = (classDescription.bar?.backgrounds ?? [])
        .map(getBackground)
        .map(({ background }) => background);

      rules.push(
        /*
      это просто кошмар какой-то, таким странным способом протаскивать в стил чарта бэкграунды самого бара,
      может я и в самом деле зря запил такого монстрика
       */
        [
          `${selector} > div`,
          [
            ['background', chartDecorations],
            [
              'border-radius',
              px2em(classDescription.bar.borderRadius ?? 0),
            ],
          ],
        ],
      );
      break;
    }
    default: {
      console.warn(`no implementation for type '${type}'`);
    }
  }

  return rules;
}
